import { useMutation } from '@apollo/client';
import type { PromptType } from '@communities/prompt-management/domain';
import { DELETE_COMMUNITY_PROMPT, GET_COMMUNITY_PROMPTS } from '@communities/prompt-management/graphql';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { EXTENDED_GRAPHQL_TIMEOUT_MS } from '@core/services/constants';
import { useState, useCallback } from 'react';

interface DeleteCommunityPromptInput {
  promptVersion: string;
  type?: PromptType;
}

interface DeleteCommunityPromptResponse {
  deleteCommunityPrompt: {
    success: boolean;
    message: string;
  };
}

export function useDeleteCommunityPrompt() {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [deletePromptMutation, { loading }] = useMutation<DeleteCommunityPromptResponse>(DELETE_COMMUNITY_PROMPT, {
    context: { timeout: EXTENDED_GRAPHQL_TIMEOUT_MS },
  });

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const deletePrompt = useCallback(
    async (communityId: string, input: DeleteCommunityPromptInput): Promise<boolean> => {
      try {
        track('DeleteCommunityPromptInitiated', {
          communityId,
          promptVersion: input.promptVersion,
          ...(input.type && { promptType: input.type }),
        });

        await deletePromptMutation({
          variables: {
            communityId,
            input,
          },
          refetchQueries: communityId ? [{ query: GET_COMMUNITY_PROMPTS, variables: { communityId } }] : [],
          awaitRefetchQueries: true,
        });

        track('DeleteCommunityPromptSucceeded', {
          communityId,
          promptVersion: input.promptVersion,
          ...(input.type && { promptType: input.type }),
        });

        return true;
      } catch (error) {
        track('DeleteCommunityPromptFailed', {
          communityId,
          promptVersion: input.promptVersion,
          ...(input.type && { promptType: input.type }),
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
        });

        setErrors(transformErrors(error));

        return false;
      }
    },
    [deletePromptMutation]
  );

  return {
    deletePrompt,
    loading,
    errors,
    resetErrors,
  };
}
